import type { ComponentProps } from 'react';
import React, { useId } from 'react';
import styled from '@emotion/styled';
import type { FlexDirectionProps, FlexProps, SpaceProps, WidthProps } from 'styled-system';
import { flex, flexDirection, space, width } from 'styled-system';

import { useMatchesViewport } from '../../hooks/media';
import { useLocalization } from '../../providers/LocaleProvider';
import type { ListStyleProps } from '../../theme/system-utilities';
import { listStyle } from '../../theme/system-utilities';
import { BodyBoldBB1 } from '../Typography';
import SuggestionsListItem from './SuggestionsListItem';
import type { SearchSuggestionsItemProps, SuggestionGetItemProps, SuggestionItem } from './types';

interface SuggestionsListProps {
  suggestions: SuggestionItem[];
  listItems: Array<SuggestionItem['name']>;
  highlightedIndex: number;
  inputValue: string;
  getItemProps: SuggestionGetItemProps<SearchSuggestionsItemProps>;
  exitSuggestions: (shouldClearInput: boolean) => void;
  shopName?: string;
}

const ListContainer = styled.ul<
  ListStyleProps & FlexProps & FlexDirectionProps & WidthProps & SpaceProps
>`
  ${listStyle};
  ${flex};
  ${flexDirection};
  ${width};
  ${space};
`;

const listContainerStyles: ComponentProps<typeof ListContainer> = {
  listStyle: 'none',
  flex: '1',
  flexDirection: 'column',
  width: 1,
  p: 'sp_0',
  m: 'sp_0',
};

const SuggestionsListContainer = styled.div<SpaceProps & WidthProps>`
  ${space};
  ${width};
`;

const suggestionsListContainerStyles = {
  p: [null, null, 'sp_24'],
  width: 1,
};

const titleStyles: ComponentProps<typeof BodyBoldBB1> = {
  as: 'h2',
  color: 'sys.neutral.text.strong',
  px: 'sp_8',
  py: 'sp_12',
};

const SuggestionsList = ({
  suggestions,
  highlightedIndex,
  getItemProps,
  ...props
}: SuggestionsListProps) => {
  const titleId = useId();
  const { t, prefix } = useLocalization();
  const {
    matches: { matchesTablet },
  } = useMatchesViewport();
  const isMobile = !matchesTablet;

  return (
    <SuggestionsListContainer
      data-testid="suggestions-list-container"
      {...suggestionsListContainerStyles}
    >
      <BodyBoldBB1 id={titleId} {...titleStyles}>
        {t('h24_search_suggestion_title')}
      </BodyBoldBB1>
      <ListContainer aria-labelledby={titleId} {...listContainerStyles}>
        {suggestions.map((item, index) => {
          item = {
            ...item,
            urlEncoded: prefix(item.urlEncoded),
          };

          return (
            <SuggestionsListItem
              key={item.name}
              {...getItemProps({
                ...props,
                item,
                index,
                // Downshift "swallows" item from the props,
                // we have to pass it with different key
                suggestionsItem: item,
                highlighted: highlightedIndex === index,
                isMobile,
                listIndex: index,
              })}
              data-cnstrc-item-name={item.name}
            />
          );
        })}
      </ListContainer>
    </SuggestionsListContainer>
  );
};

export default SuggestionsList;
